import { useEffect, useState } from "react";
import moment from "moment";

import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { BillingInbox } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { ExportCsvModel } from "../Components/PrivateBilling/Model/ExportCsvModel";
import { BillingHelper } from "../Helpers/BillingHelper";
import { PayableInboxFilterSettings } from "../Models/Settings/PayableInboxFilterSettings";
import { IBillingTabModel } from "../Models/UI/IBillingTabModel";
import { ISalesInboxModel } from "../Models/UI/ISalesInboxModel";
import { ITransactionsInboxModel } from "../Models/UI/ITransactionsInboxModel";

import styles from "./BillingTreeControl.module.css";

interface IBillingTreeControlProps {
    CurrentInbox: BillingInbox;
    TransactionsInboxModel: ITransactionsInboxModel;
    SalesInboxModel: ISalesInboxModel;
    BillingModel: IBillingTabModel;
    payableBillsFilterSettings: PayableInboxFilterSettings;
    ClaimStatusCount: number;
}

function BillingTreeControl(props: IBillingTreeControlProps): JSX.Element {
    const { getText } = useTranslation();

    function getTransactionData(transactionModel: ITransactionsInboxModel): ExportCsvModel {
        let requestData = inputData();
        let dateFrom = transactionModel.get("DateFrom");
        requestData.FromDate = dateFrom ? moment(dateFrom).format("YYYY/MM/DD") : "";

        let dateTo = transactionModel.get("DateTo");
        requestData.ToDate = dateTo ? moment(dateTo).format("YYYY/MM/DD") : "";

        let visitDateForm = transactionModel.get("VisitDateFrom");
        requestData.VisitFromDate = LuxonDateTimeParser.parse(visitDateForm)?.toFormat("yyyy/LL/dd") ?? "";

        let visitDateTo = transactionModel.get("VisitDateTo");
        requestData.VisitToDate = LuxonDateTimeParser.parse(visitDateTo)?.toFormat("yyyy/LL/dd") ?? "";

        if (cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
            const paymentFromDate = transactionModel.get("PaymentDateFrom");
            requestData.PaymentFromDate = LuxonDateTimeParser.parse(paymentFromDate)?.toFormat("yyyy/LL/dd") ?? "";

            const paymentToDate = transactionModel.get("PaymentDateTo");
            requestData.PaymentToDate = LuxonDateTimeParser.parse(paymentToDate)?.toFormat("yyyy/LL/dd") ?? "";
        }

        requestData.IsFromTransactions = "true";
        requestData.IsFromSales = "false";

        if (transactionModel && transactionModel.get("Status")) {
            transactionModel.get("Status").forEach(status => {
                if (status.Id === "closed_status" && status.Checked === true)
                    requestData.IsClosed = "true";
                if (status.Id === "open_status" && status.Checked === true)
                    requestData.IsOpen = "true";
            });
        }

        if (transactionModel && transactionModel.get("Validity")) {
            transactionModel.get("Validity").forEach(status => {
                if (status.Id === "valid" && status.Checked === true)
                    requestData.IsValid = "true";
                if (status.Id === "invalid" && status.Checked === true)
                    requestData.IsInvalid = "true";
            });
        }

        if (transactionModel && transactionModel.get("ProviderFiltered")) {
            transactionModel.get("ProviderFiltered").forEach(employee => {
                if (employee.Checked) {
                    if (requestData.ProviderIds === "")
                        requestData.ProviderIds = employee.ContactFacetId;
                    else
                        requestData.ProviderIds += "," + employee.ContactFacetId;
                }
            });
        }

        if (transactionModel && transactionModel.get("BilledByFiltered")) {
            transactionModel.get("BilledByFiltered").forEach(employee => {
                if (employee.Checked) {
                    if (requestData.BilledByIds === "")
                        requestData.BilledByIds = employee.ContactFacetId;
                    else
                        requestData.BilledByIds += "," + employee.ContactFacetId;
                }
            });
        }

        if (transactionModel && transactionModel.get("Sites")) {
            var showOnlySelectedSitesTransactions = transactionModel.get("ShowOnlySelectedSites");
            transactionModel.get("Sites").forEach(site => {
                if (site.Checked && showOnlySelectedSitesTransactions) {
                    if (requestData.SiteIds === "")
                        requestData.SiteIds = site.Id;
                    else
                        requestData.SiteIds += "," + site.Id;
                }
            });
        }

        if (transactionModel && transactionModel.get("PaymentMethod")) {
            transactionModel.get("PaymentMethod").forEach(method => {
                if (method.Checked) {
                    if (requestData.PaymentMethod === "")
                        requestData.PaymentMethod = method.Id;
                    else
                        requestData.PaymentMethod += "," + method.Id;
                }
            });
        }

        requestData.SearchText = transactionModel.get("GlobalTransactionsSearchText");
        return requestData;
    }

    function getSalesInboxData(salesInboxModel: ISalesInboxModel): ExportCsvModel {
        let requestData = inputData();

        let dateFrom = salesInboxModel.get("DateFrom");
        requestData.FromDate = dateFrom ? moment(dateFrom).format("YYYY/MM/DD") : "";

        let dateTo = salesInboxModel.get("DateTo");
        requestData.ToDate = dateTo ? moment(dateTo).format("YYYY/MM/DD") : "";

        let visitDateForm = salesInboxModel.get("VisitFromDate");
        requestData.VisitFromDate = LuxonDateTimeParser.parse(visitDateForm)?.toFormat("yyyy/LL/dd") ?? "";

        let visitDateTo = salesInboxModel.get("VisitToDate");
        requestData.VisitToDate = LuxonDateTimeParser.parse(visitDateTo)?.toFormat("yyyy/LL/dd") ?? "";

        requestData.IsFromTransactions = "false";
        requestData.IsFromSales = "true";

        if (salesInboxModel && salesInboxModel.get("Status")) {
            salesInboxModel.get("Status").forEach(status => {
                if (status.Id === "closed_status" && status.Checked === true)
                    requestData.IsClosed = "true";
                if (status.Id === "open_status" && status.Checked === true)
                    requestData.IsOpen = "true";
            });
        }

        if (salesInboxModel && salesInboxModel.get("Validity")) {
            salesInboxModel.get("Validity").forEach(status => {
                if (status.Id === "valid" && status.Checked === true)
                    requestData.IsValid = "true";
                if (status.Id === "invalid" && status.Checked === true)
                    requestData.IsInvalid = "true";
            });
        }

        if (salesInboxModel && salesInboxModel.get("ProviderFiltered")) {
            salesInboxModel.get("ProviderFiltered").forEach(employee => {
                if (employee.Checked) {
                    if (requestData.ProviderIds === "")
                        requestData.ProviderIds = employee.ContactFacetId;
                    else
                        requestData.ProviderIds += "," + employee.ContactFacetId;
                }
            });
        }

        if (salesInboxModel && salesInboxModel.get("BilledByFiltered")) {
            salesInboxModel.get("BilledByFiltered").forEach(employee => {
                if (employee.Checked) {
                    if (requestData.BilledByIds === "")
                        requestData.BilledByIds = employee.ContactFacetId;
                    else
                        requestData.BilledByIds += "," + employee.ContactFacetId;
                }
            });
        }

        if (salesInboxModel && salesInboxModel.get("Sites")) {
            var showOnlySelectedSitesSales = salesInboxModel.get("ShowOnlySelectedSites");
            salesInboxModel.get("Sites").forEach(site => {
                if (site.Checked && showOnlySelectedSitesSales) {
                    if (requestData.SiteIds === "")
                        requestData.SiteIds = site.Id;
                    else
                        requestData.SiteIds += "," + site.Id;
                }
            });
        }

        if (salesInboxModel && salesInboxModel.get("PaymentMethod")) {
            salesInboxModel.get("PaymentMethod").forEach(method => {
                if (method.Checked) {
                    if (requestData.PaymentMethod === "")
                        requestData.PaymentMethod = method.Id;
                    else
                        requestData.PaymentMethod += "," + method.Id;
                }
            });
        }

        requestData.SearchText = salesInboxModel.get("GlobalSalesSearchText");

        return requestData;
    }

    function inputData(): ExportCsvModel {
        const exportmodel = new ExportCsvModel();
        exportmodel.FromDate = "";
        exportmodel.ToDate = "";
        exportmodel.VisitFromDate = "";
        exportmodel.VisitToDate = "";
        exportmodel.SiteIds = "";
        exportmodel.ProviderIds = "";
        exportmodel.BilledByIds = "";
        exportmodel.PaymentMethod = "";
        exportmodel.IsFromSales = "";
        exportmodel.IsFromTransactions = "";
        exportmodel.SearchText = "";
        exportmodel.IsClosed = "";
        exportmodel.IsOpen = "";
        exportmodel.IsValid = "";
        exportmodel.IsInvalid = "";
        exportmodel.PaymentFromDate = "";
        exportmodel.PaymentToDate = "";
        return exportmodel;
    }

    function getInputData(): ExportCsvModel {
        let requestData = inputData();
        if (props.CurrentInbox === BillingInbox.Transactions) {
            requestData = getTransactionData(props.TransactionsInboxModel);
        }
        else if (props.CurrentInbox === BillingInbox.Sales) {
            requestData = getSalesInboxData(props.SalesInboxModel);
        }

        return requestData;
    }

    const [requestData, setrequestData] = useState(getInputData());
    const [billingClaimStatusCount, setbillingClaimStatusCount] = useState(props.ClaimStatusCount);

    function onClickTransaction(e: React.MouseEvent): void {
        e.preventDefault();
        if (props.CurrentInbox === BillingInbox.Transactions
            || props.CurrentInbox === BillingInbox.Sales
            || props.CurrentInbox === BillingInbox.Payable) {
            document.getElementById("csvform").dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
            // we use dispatchEvent because programmatic submit like `document.forms["csvform"].submit()` does not trigger
        }
    }

    async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
        event.preventDefault();

        if (props.CurrentInbox === BillingInbox.Payable) {
            await BillingHelper.exportPayableCsv(props.payableBillsFilterSettings);
        }
        else if (props.CurrentInbox === BillingInbox.Transactions
            || props.CurrentInbox === BillingInbox.Sales) {
            const formData = new FormData(document.forms["csvform"]);
            let params = {} as any;
            formData.forEach((value, key) => params[key] = value);
            params["SiteIds"] = params["SiteIds"]?.split(",");
            params["PaymentMethod"] = params["PaymentMethod"]?.split(",");
            params["ProviderIds"] = params["ProviderIds"]?.split(",");
            params["BilledByIds"] = params["BilledByIds"]?.split(",");

            await BillingHelper.exportBillingCsv(params, props.CurrentInbox);
        }
    }

    useEffect(() => {
        setrequestData(getInputData());
        if (cacheStore.ClinicSettings.EnableBillingReconciliationReport)
            setbillingClaimStatusCount(props.ClaimStatusCount);
    }, [props.CurrentInbox, props.ClaimStatusCount, props.TransactionsInboxModel, props.SalesInboxModel]);

    return (
        <div data-cy="billingTreeControl">
            <form
                onSubmit={e => onSubmit(e)}
                name="csvform"
                id="csvform"
            >
                {requestData.IsFromTransactions && requestData.IsFromTransactions.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="IsFromTransactions" value={requestData.IsFromTransactions} />}
                {requestData.FromDate && requestData.FromDate.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="FromDate" value={requestData.FromDate} />}
                {requestData.ToDate && requestData.ToDate.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="ToDate" value={requestData.ToDate} />}
                {requestData.VisitFromDate && requestData.VisitFromDate.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="VisitFromDate" value={requestData.VisitFromDate} />}
                {requestData.VisitToDate && requestData.VisitToDate.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="VisitToDate" value={requestData.VisitToDate} />}
                {requestData.SiteIds && requestData.SiteIds.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="SiteIds" value={requestData.SiteIds} />}
                {requestData.ProviderIds && requestData.ProviderIds.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="ProviderIds" value={requestData.ProviderIds} />}
                {requestData.BilledByIds && requestData.BilledByIds.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="BilledByIds" value={requestData.BilledByIds} />}
                {requestData.PaymentMethod && requestData.PaymentMethod.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="PaymentMethod" value={requestData.PaymentMethod} />}
                {requestData.IsFromSales && requestData.IsFromSales.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="IsFromSales" value={requestData.IsFromSales} />}
                {requestData.SearchText && requestData.SearchText.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="SearchText" value={requestData.SearchText} />}
                {requestData.IsClosed && requestData.IsClosed.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="IsClosed" value={requestData.IsClosed} />}
                {requestData.IsOpen && requestData.IsOpen.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="IsOpen" value={requestData.IsOpen} />}
                {requestData.IsValid && requestData.IsValid.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="IsValid" value={requestData.IsValid} />}
                {requestData.IsInvalid && requestData.IsInvalid.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="IsInvalid" value={requestData.IsInvalid} />}
                {requestData.PaymentFromDate && requestData.PaymentFromDate.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="PaymentFromDate" value={requestData.PaymentFromDate} />}
                {requestData.PaymentToDate && requestData.PaymentToDate.length > 0 && <input type="test" style={{ display: "none" }} readOnly={true} name="PaymentToDate" value={requestData.PaymentToDate} />}
            </form>
            {cacheStore.ClinicSettings.EnableBillingReconciliationReport ? (
                <ul className="accordion leftNavAccordion" data-accordion="">
                    {BillingHelper.isPrivateBillPermission() && (
                        <li className="accordion-navigation leftNavAccordion">
                            <div className="displaced-nav-btns">
                                <div>
                                    <h4 className={`bold ${styles.leftMenuRootTitleColor}`}>
                                        {`${getText("PrivateBillingLabel")} : `}
                                    </h4>
                                </div>
                                <div className={`pull-left ${styles.childTreeMargin}`}>
                                    <div>
                                        <HyperLinkControl
                                            IsUnderLined={props.CurrentInbox === BillingInbox.Transactions}
                                            operation={LocalOperations.Billing.SwitchTo_TransactionsInbox}
                                            text={getText("TransactionsBillingLabel")}
                                        />
                                    </div>
                                    <div className="padding-sm-top">
                                        <HyperLinkControl
                                            IsUnderLined={props.CurrentInbox === BillingInbox.Sales}
                                            operation={LocalOperations.Billing.SwitchTo_SalesInbox}
                                            text={getText("SalesBillingLabel")}
                                        />
                                    </div>
                                    {cacheStore.ClinicSettings.EnablePrivateBillRecipient && (
                                        <div className="padding-sm-top">
                                            <HyperLinkControl
                                                IsUnderLined={props.CurrentInbox == BillingInbox.Payable}
                                                operation={LocalOperations.Billing.SwitchTo_PayableInbox}
                                                text={cacheStore.GetString("ReceivablesLabel")}
                                                dataCy="PayableBillsTreeControlLink"
                                            />
                                        </div>
                                    )}
                                    <div className="padding-sm-top">
                                        <a href="#" onClick={e => onClickTransaction(e)}> {getText("ExportCSVLabel")}</a>
                                    </div>
                                </div>

                            </div>
                        </li>
                    )}
                    {BillingHelper.isPublicBillPermission() && (
                        <li className="accordion-navigation leftNavAccordion">
                            <div className="displaced-nav-btns">
                                <div data-cy="public-billing">
                                    <h4 className={`bold ${styles.leftMenuRootTitleColor}`}>
                                        {`${getText("BillingReconciliation_PublicBillingLabel")} : `}
                                    </h4>
                                </div>
                                <div className={`pull-left ${styles.childTreeMargin}`}>
                                    <div data-cy="public-billing-claim-status">
                                        <HyperLinkControl
                                            IsUnderLined={props.CurrentInbox === BillingInbox.BillingClaimStatus}
                                            operation={LocalOperations.Billing.BillingClaimStatusInbox}
                                            text={`${getText("BillingReconciliation_PaymentRequestStatusLabel")} (${billingClaimStatusCount ?? 0})`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </li>
                    )}
                </ul>
            ) : (
                <ul className="accordion leftNavAccordion" data-accordion="">
                    <li className="accordion-navigation leftNavAccordion">
                        <div className="displaced-nav-btns">
                            <div className="pull-left">
                                <div>
                                    <HyperLinkControl
                                        IsUnderLined={props.CurrentInbox === BillingInbox.Transactions}
                                        operation={LocalOperations.Billing.SwitchTo_TransactionsInbox}
                                        text={getText("TransactionsBillingLabel")}
                                    />
                                </div>
                                <div className="padding-sm-top">
                                    <HyperLinkControl
                                        IsUnderLined={props.CurrentInbox === BillingInbox.Sales}
                                        operation={LocalOperations.Billing.SwitchTo_SalesInbox}
                                        text={getText("SalesBillingLabel")}
                                    />
                                </div>
                                {cacheStore.ClinicSettings.EnablePrivateBillRecipient && (
                                    <div className="padding-sm-top">
                                        <HyperLinkControl
                                            IsUnderLined={props.CurrentInbox == BillingInbox.Payable}
                                            operation={LocalOperations.Billing.SwitchTo_PayableInbox}
                                            text={cacheStore.GetString("ReceivablesLabel")}
                                            dataCy="PayableBillsTreeControlLink"
                                        />
                                    </div>
                                )}
                                <div className="padding-sm-top">
                                    <a href="#" onClick={e => onClickTransaction(e)}> {getText("ExportCSVLabel")}</a>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            )
            }
        </div>
    );
}

export { BillingTreeControl };
